import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Header } from '@/components/Header/Header';
import { CategoryBar } from '@/components/CategoryBar/CategoryBar';
import { TagBar } from '@/components/TagBar/TagBar';
import { DateRangeSlider } from '@/components/DateRangeSlider/DateRangeSlider';
import { ItemGrid } from '@/components/ItemGrid/ItemGrid';
import { ItemModal } from '@/components/ItemModal/ItemModal';
import { Screensaver } from '@/components/Screensaver/Screensaver';
import { OfflineBanner } from '@/components/OfflineBanner/OfflineBanner';
import { useLanguage } from '@/hooks/useLanguage';
import { useCategories } from '@/hooks/useCategories';
import { useTags } from '@/hooks/useTags';
import { useItems } from '@/hooks/useItems';
import { useItemDetail } from '@/hooks/useItemDetail';
import { useIdleTimer } from '@/hooks/useIdleTimer';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useEventDateRange } from '@/hooks/useEventDateRange';
import { useSiteSettings } from '@/hooks/useSiteSettings';

// Egy nyilvános, felügyelet nélküli kiosknál 90 mp inaktivitás után váltunk screensaverre.
const IDLE_TIMEOUT_MS = 90_000;

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Helyi (nem UTC) 'YYYY-MM-DD' — toISOString() UTC-re vált, ami éjfél körül napot csúsztatna. */
function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const UI_TEXT: Record<string, Record<string, string>> = {
  hu: {
    all: 'Összes',
    empty: 'Nincs a szűrésnek megfelelő elem.',
    qr_hint: 'Olvasd be a kódot a weboldal megnyitásához!',
    offline: 'Nincs internetkapcsolat — az adatok elavultak lehetnek.',
    date_range: 'Időszak',
  },
  en: {
    all: 'All',
    empty: 'No items match this filter.',
    qr_hint: 'Scan the code to open the website!',
    offline: 'No internet connection — data may be outdated.',
    date_range: 'Date range',
  },
  de: {
    all: 'Alle',
    empty: 'Keine Einträge für diesen Filter.',
    qr_hint: 'Scanne den Code, um die Website zu öffnen!',
    offline: 'Keine Internetverbindung — Daten könnten veraltet sein.',
    date_range: 'Zeitraum',
  },
};

export function App() {
  const { lang, setLang, supported } = useLanguage();
  const t = UI_TEXT[lang] ?? UI_TEXT.hu;

  const [categoryId, setCategoryId] = useState(0);
  const [tagId, setTagId] = useState(0);
  const [openItemId, setOpenItemId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const { isIdle } = useIdleTimer(IDLE_TIMEOUT_MS);
  const isOnline = useNetworkStatus();

  const today = useMemo(() => startOfToday(), []);
  const dateRangeQuery = useEventDateRange();
  const maxEventDate = useMemo(() => {
    const raw = dateRangeQuery.data?.max_date;
    if (!raw) return null;
    const parsed = new Date(`${raw}T00:00:00`);
    return parsed >= today ? parsed : null; // ha minden esemény már elmúlt, nincs értelme csúszkát mutatni
  }, [dateRangeQuery.data, today]);

  // Alapértelmezett tartomány: ma → a legkésőbbi esemény dátuma (mindig induláskor/friss adatnál).
  useEffect(() => {
    if (maxEventDate) {
      setDateFrom(today);
      setDateTo(maxEventDate);
    }
  }, [maxEventDate, today]);

  // Screensaverbe lépéskor visszaáll az alapállapotra, hogy a következő látogató tiszta lappal induljon.
  useEffect(() => {
    if (isIdle) {
      setOpenItemId(null);
      setCategoryId(0);
      setTagId(0);
      if (maxEventDate) {
        setDateFrom(today);
        setDateTo(maxEventDate);
      }
    }
  }, [isIdle, maxEventDate, today]);

  // Kategóriaváltáskor a korábban kiválasztott altag elveszti az érvényességét (más kategória
  // altagje lehetett) — nullázzuk, hogy ne maradjon egy láthatatlanul aktív szűrő.
  useEffect(() => {
    setTagId(0);
  }, [categoryId]);

  const categoriesQuery = useCategories(lang);
  const tagsQuery = useTags(lang);
  const siteSettingsQuery = useSiteSettings();

  // Globális tag-ek mindig látszanak; kategória-specifikus altag csak akkor, ha épp az a
  // kategória van kiválasztva ("Összes" nézetben, categoryId=0, csak a globálisak látszanak).
  const visibleTags = useMemo(
    () => (tagsQuery.data ?? []).filter((tag) => tag.category_id === null || tag.category_id === categoryId),
    [tagsQuery.data, categoryId],
  );

  const filter = useMemo(
    () => ({
      categoryId: categoryId || undefined,
      tagId: tagId || undefined,
      dateFrom: dateFrom ? toIsoDate(dateFrom) : undefined,
      dateTo: dateTo ? toIsoDate(dateTo) : undefined,
    }),
    [categoryId, tagId, dateFrom, dateTo],
  );
  const itemsQuery = useItems(lang, filter);
  const itemDetailQuery = useItemDetail(lang, openItemId);

  return (
    <div className="h-screen overflow-hidden relative bg-kiosk-bg">
      <OfflineBanner isOnline={isOnline} message={t.offline} />
      <Header lang={lang} supported={supported} onLangChange={setLang} logoUrl={siteSettingsQuery.data?.header_logo} />

      {/* Egyetlen görgethető réteg: a kategória/tag sáv és a kártyák együtt csúsznak
          a lebegő fejléc alá/alatt — ezért van a felső padding, ami a fejléc helyét tartja. */}
      <div className="h-full overflow-y-auto pt-20 kiosk:pt-[7vw]">
        <CategoryBar
          categories={categoriesQuery.data ?? []}
          activeId={categoryId}
          onSelect={setCategoryId}
          allLabel={t.all}
        />

        <TagBar
          tags={visibleTags}
          activeId={tagId}
          onToggle={(id) => setTagId((current) => (current === id ? 0 : id))}
        />

        {maxEventDate && dateFrom && dateTo && (
          <DateRangeSlider
            lang={lang}
            minDate={today}
            maxDate={maxEventDate}
            valueFrom={dateFrom}
            valueTo={dateTo}
            onChange={(from, to) => {
              setDateFrom(from);
              setDateTo(to);
            }}
            label={t.date_range}
          />
        )}

        <ItemGrid
          items={itemsQuery.data ?? []}
          isLoading={itemsQuery.isLoading}
          onOpen={setOpenItemId}
          emptyLabel={t.empty}
        />
      </div>

      <ItemModal
        item={itemDetailQuery.data}
        isLoading={itemDetailQuery.isLoading}
        lang={lang}
        onClose={() => setOpenItemId(null)}
        qrHint={t.qr_hint}
      />

      <AnimatePresence>
        {isIdle && <Screensaver videoSrc={siteSettingsQuery.data?.screensaver_video ?? undefined} />}
      </AnimatePresence>
    </div>
  );
}
